import React from 'react';
import { Button } from 'antd';
import { useIntl, useSelector } from 'umi';
import * as TaskAutomationSelectors from '@/selectors/taskAutomationSelectors';
import { TaskAutomationMetaModel, TaskAutomationMetaType } from '@/typings/models/TaskAutomationModel';
import {
  RETIRE_TRIGGER_TYPES,
  TASK_AUTOMATION_DEFAULT_MESSAGES,
  TaskAutomationButtonType,
  TaskAutomationConsequenceType,
  TaskAutomationCreateFor,
  TaskAutomationReferenceType,
  TaskAutomationTriggerType,
} from './constants';

// Helper function for translations
export const useTaskAutomationTranslation = () => {
  const intl = useIntl();
  return (id, values?: Record<string, any>) => intl.formatMessage({
    id: `taskAutomation.${id}`,
    defaultMessage: TASK_AUTOMATION_DEFAULT_MESSAGES[id] || id,
  }, values);
};

// Condition fields that are plain entities reuse the shared entity names; the rest keep
// their own taskAutomation.condition_field.* key.
const CONDITION_FIELD_ENTITY_IDS: Record<string, string> = {
  branch: 'entity.branch.singular',
  service_type: 'entity.service_type.singular',
  offering: 'entity.offering.singular',
  product_group: 'entity.product_group.singular',
};

/** Label of a named condition field; unknown keys come back unchanged. */
export const useConditionFieldLabel = () => {
  const intl = useIntl();
  const t = useTaskAutomationTranslation();
  return (key: string): string => {
    const entityId = CONDITION_FIELD_ENTITY_IDS[key];
    if (entityId) return intl.formatMessage({ id: entityId });
    const translated = t(`condition_field.${key}`);
    return translated !== `condition_field.${key}` ? translated : key;
  };
};

type Translate = (id: string, values?: Record<string, any>) => string;

// Label resolution: our translation -> API description -> raw type. Unknown
// (future) backend types still render with the API-provided description.
const resolveTypeLabel = (
  t: Translate,
  prefix: string,
  type: string,
  metaTypes?: TaskAutomationMetaType[],
) => {
  const translated = t(`${prefix}.${type}`);
  if (translated !== `${prefix}.${type}`) return translated;
  const metaType = metaTypes?.find((entry) => entry.type === type);
  return metaType?.description || type;
};

const groupTypes = (types: string[], groups: Record<string, string[]>): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};
  const known = new Set<string>();
  Object.entries(groups).forEach(([group, groupTypesList]) => {
    grouped[group] = groupTypesList.filter((type) => types.includes(type));
    groupTypesList.forEach((type) => known.add(type));
  });
  const rest = types.filter((type) => !known.has(type));
  if (rest.length) grouped.Other = rest;
  return grouped;
};

/**
 * Option lists for the editor — structure comes from the /details metadata
 * when available (so new backend types appear without a release) and falls
 * back to the local constants when the fetch failed.
 */
export const useTaskAutomationOptions = () => {
  const t = useTaskAutomationTranslation();
  const meta: TaskAutomationMetaModel | null = useSelector(TaskAutomationSelectors.selectTaskAutomationMeta);

  const triggerTypes = meta?.trigger_types?.length
    ? meta.trigger_types.map((entry) => entry.type)
    : [...Object.values(TaskAutomationTriggerType.Lead), ...Object.values(TaskAutomationTriggerType.Meeting)];

  const triggerGroups = groupTypes(triggerTypes, {
    Lead: Object.values(TaskAutomationTriggerType.Lead),
    Meeting: Object.values(TaskAutomationTriggerType.Meeting),
  });

  const triggerOptions = Object.entries(triggerGroups)
    .filter(([, types]) => types.length > 0)
    .map(([group, types]) => ({
      label: t(`retire_trigger.group_${group.toLowerCase()}`),
      options: types.map((value) => ({
        label: resolveTypeLabel(t, 'trigger', value, meta?.trigger_types),
        value,
      })),
    }));

  const consequenceTypes = meta?.consequence_types?.length
    ? meta.consequence_types.map((entry) => entry.type)
    : Object.values(TaskAutomationConsequenceType);

  const consequenceOptions = consequenceTypes.map((value) => ({
    label: resolveTypeLabel(t, 'consequence', value, meta?.consequence_types),
    value,
  }));

  const retireTypes = meta?.retire_trigger_types?.length
    ? meta.retire_trigger_types.map((entry) => entry.type)
    : [...RETIRE_TRIGGER_TYPES.Lead, ...RETIRE_TRIGGER_TYPES.Meeting];

  const retireGroups = groupTypes(retireTypes, {
    Lead: RETIRE_TRIGGER_TYPES.Lead,
    Meeting: RETIRE_TRIGGER_TYPES.Meeting,
  });

  const retireOptions = [
    {
      label: t('retire_trigger.group_time'),
      options: [{
        label: t(`retire_trigger.${TaskAutomationTriggerType.Time.DaysPassed}`),
        value: TaskAutomationTriggerType.Time.DaysPassed,
      }],
    },
    ...Object.entries(retireGroups)
      .filter(([, types]) => types.length > 0)
      .map(([group, types]) => ({
        label: t(`retire_trigger.group_${group.toLowerCase()}`),
        options: types.map((value) => ({
          label: resolveTypeLabel(t, 'retire_trigger', value, meta?.retire_trigger_types),
          value,
        })),
      })),
  ];

  const createForValues = meta?.task_create_for_options?.length
    ? meta.task_create_for_options
    : Object.values(TaskAutomationCreateFor);

  const createForOptions = createForValues.map((value) => ({
    label: t(`create_for.${value}`),
    value,
  }));

  const referenceValues = meta?.task_time_creation_based_on_options?.length
    ? meta.task_time_creation_based_on_options
    : Object.values(TaskAutomationReferenceType);

  const referenceOptions = referenceValues.map((value) => ({
    label: t(`reference.${value}`),
    value,
  }));

  const buttonTypeOptions = Object.values(TaskAutomationButtonType).map((value) => ({
    label: t(`button_type.${value}`),
    value,
  }));

  return {
    meta,
    triggerOptions,
    consequenceOptions,
    retireOptions,
    createForOptions,
    referenceOptions,
    buttonTypeOptions,
  };
};

// Render button preview function
export const renderButtonPreview = ({ ui_widget, button_label, ...props }, t) => {
  if (!button_label) return null;
  const label = button_label || (t ? t('form.button') : 'Button');

  switch (ui_widget) {
    case TaskAutomationButtonType.Danger:
      return <Button size={'small'} color={'danger'} variant="solid" {...props}>{label}</Button>;
    case TaskAutomationButtonType.Filled:
      return <Button size={'small'} color={'primary'} variant={'filled'} {...props}>{label}</Button>;
    case TaskAutomationButtonType.Solid:
      return <Button size={'small'} color={'primary'} variant={'solid'} {...props}>{label}</Button>;
    case TaskAutomationButtonType.Text:
      return <Button size={'small'} type="text" {...props}>{label}</Button>;
    case TaskAutomationButtonType.Link:
      return <Button size={'small'} type="link" {...props}>{label}</Button>;
    case TaskAutomationButtonType.Blue:
      return <Button size={'small'} color={'geekblue'} variant={'solid'} {...props}>{label}</Button>;
    case TaskAutomationButtonType.Normal:
    default:
      return <Button size={'small'} {...props}>{label}</Button>;
  }
};

/**
 * The editor keeps the task due offset in days, and anything under a day is a
 * fraction (240 minutes is 0.1666… days). It is shown in the largest whole unit
 * (days, hours, else minutes) so the sentence never prints a long decimal.
 */
export const dueOffsetDisplay = (days: number): (
  { unit: 'days'; days: number } | { unit: 'hours'; hours: number } | { unit: 'minutes'; minutes: number }
) & { before: boolean } => {
  const before = days < 0;
  const minutes = Math.round(Math.abs(days) * 24 * 60);
  if (minutes % (24 * 60) === 0) return { unit: 'days', days: minutes / (24 * 60), before };
  if (minutes % 60 === 0) return { unit: 'hours', hours: minutes / 60, before };
  return { unit: 'minutes', minutes, before };
};

/** Translation key of a due-offset sentence: `<base>`, `<base>_hours`, `<base>_minutes`, each with a `_before` variant. */
export const dueOffsetMessageKey = (base: string, due: ReturnType<typeof dueOffsetDisplay>) =>
  `${base}${due.unit === 'days' ? '' : `_${due.unit}`}${due.before ? '_before' : ''}`;
